import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import CustomTable from "../../../components/table/CustomTable";
import StatusChip from "../../../components/statusChip/StatusChip";
import { formatMomentDate } from "../../../utils/dateHelper";
import { TableSearch } from "../../../components/form/Fields";
import { downloadPaymentInvoice } from "../../../redux/slice/subscriptionSlice";
import { useToast } from "../../../context/TostContext";
import { saveAs } from "file-saver";
import { UserInital } from "../../../utils/otherHelopers";

const getPaymentStatusBg = (status) => {
    switch (status?.toLowerCase()) {
        case "paid":
        case "success":
            return "success";
        case "failed":
            return "danger";
        case "pending":
        case "user_dropped":
            return "warning";
        default:
            return "";
    }
};

const parsePaymentMethodDetails = (details, group) => {
    if (!details) return "-";
    const method = details.payment_method || details;
    if (group === "upi" && method.upi) {
        return `UPI: ${method.upi.upi_id || "N/A"}`;
    }
    if (group === "card" && method.card) {
        const { card_bank_name, card_network, card_type, card_number } = method.card;
        const typeFormatted = card_type ? card_type.replace("_", " ") : "";
        return [card_bank_name, card_network, typeFormatted, card_number ? `ending in ${card_number}` : ""]
            .filter(Boolean)
            .join(" ")
            .toUpperCase();
    }
    return group ? group.toUpperCase() : "-";
};

const List = ({
    data,
    searchTerm,
    setSearchTerm,
    setCurrentPage,
    currentPage,
    limit,
    setLimit,
    total,
}) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const [loadingId, setLoadingId] = useState(null);

    const handleDownloadInvoice = (item) => {
        setLoadingId(item.id);
        dispatch(downloadPaymentInvoice({ payment_transaction_id: item.id }))
            .unwrap()
            .then((data) => {
                if (data?.base64) {
                    const byteCharacters = atob(data.base64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: "application/pdf" });

                    saveAs(blob, data.filename || `invoice-${item.invoice_no ? item.invoice_no.replace(/\//g, "-") : item.id}.pdf`);
                } else {
                    toast.error("Invoice data not available.");
                }
            })
            .catch((err) => {
                toast.error(err?.message || "Failed to download invoice.");
            })
            .finally(() => {
                setLoadingId(null);
            });
    };

    const headerPortalTarget = document.getElementById("header-portal-target");

    const headerContent = (
        <div className="d-flex align-items-center justify-content-between w-100">
            <h4 className="fw-semibold mb-0 text-primary fs-lg">
                <span className="text-muted fw-normal">Subscription / </span>Payment Transactions
            </h4>
        </div>
    );

    const columns = [
        {
            header: "User",
            cell: (_, item) => (
                <div className="d-flex align-items-center gap-3 text-nowrap">
                    <UserInital image={item.user?.profile?.profile_photo?.url} name={`${item.user?.first_name || ""} ${item.user?.last_name || ""}`.trim() || "-"} />
                    <div>
                        <p className="fw-semibold">
                            {item.user
                                ? `${item.user.first_name || ""} ${item.user.last_name || ""}`.trim() || "-"
                                : "-"}
                        </p>
                        <span className="text-muted fs-sm">{item.user?.mobile_number || "-"}</span>
                        <small className="text-muted d-block w-100">{item.user?.email || "-"}</small>
                    </div>
                </div>
            ),
        },
        {
            header: "Plan",
            cell: (_, item) => (
                <>
                    <span className="fw-medium text-nowrap">{item.subscription?.plan?.name || "-"}</span>
                    <p className="text-muted fs-sm text-capitalize text-nowrap">
                        {item.subscription?.plan?.interval || "-"}
                    </p>
                    {item.subscription?.source && (
                        <small className="text-capitalize text-muted text-nowrap">via {item.subscription.source}</small>
                    )}
                </>
            ),
        },
        {
            header: "Payment Method",
            cell: (_, item) => {
                const group = item.payment_group || item.payment_method?.type;
                const details = item.payment_details || item.payment_method;
                return (
                    <>
                        <p className="text-capitalize fw-medium text-nowrap">{group || "-"}</p>
                        {details && (
                            <small className="text-muted text-nowrap">
                                {parsePaymentMethodDetails(details, group)}
                            </small>
                        )}
                    </>
                );
            },
        },
        {
            header: "Transaction ID",
            cell: (_, item) => {
                const id =
                    item.cashfree_payment_id ||
                    item.cf_payment_id ||
                    item.payment_details?.payment_id;
                return id ? (
                    <div className="text-nowrap" style={{ fontSize: "13px", fontFamily: "monospace" }}>
                        {id}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Status",
            cell: (_, item) =>
                item.status ? (
                    <StatusChip
                        bg={getPaymentStatusBg(item.status)}
                        pill
                        title={item.status}
                        className="text-capitalize"
                    />
                ) : (
                    "-"
                ),
        },
        {
            header: "Invoice No",
            cell: (_, item) =>
                item.invoice_no ? (
                    <div className="text-nowrap" style={{ fontSize: "13px", fontFamily: "monospace" }}>
                        {item.invoice_no}
                    </div>
                ) : (
                    "-"
                ),
        },
        {
            header: "Start Date",
            cell: (_, item) =>
                item.start_date ? (
                    <div className="d-flex flex-column text-nowrap">
                        <span>{formatMomentDate(item.start_date, "DD MMM YYYY")}</span>
                        <small className="text-muted">{formatMomentDate(item.start_date, "hh:mm A")}</small>
                    </div>
                ) : (
                    "-"
                ),
        },
        {
            header: "End Date",
            cell: (_, item) =>
                item.end_date ? (
                    <div className="d-flex flex-column text-nowrap">
                        <span>{formatMomentDate(item.end_date, "DD MMM YYYY")}</span>
                        <small className="text-muted">{formatMomentDate(item.end_date, "hh:mm A")}</small>
                    </div>
                ) : (
                    "-"
                ),
        },
        {
            header: "Currency",
            cell: (_, item) => (
                <div className="text-nowrap fw-medium">{item.currency || "-"}</div>
            ),
        },
        {
            header: "Base Amount",
            cell: (_, item) => {
                const amount = item.base_amount;
                return amount != null ? (
                    <div className="text-nowrap">
                        {item.currency || "INR"} {parseFloat(amount).toLocaleString("en-IN")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "GST %",
            cell: (_, item) => {
                const gst = item.gst_percentage;
                return gst != null ? (
                    <div className="text-nowrap">{parseFloat(gst)}%</div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Tax Amount",
            cell: (_, item) => {
                const tax = item.tax_amount;
                return tax != null ? (
                    <div className="text-nowrap">
                        {item.currency || "INR"} {parseFloat(tax).toLocaleString("en-IN")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Payable Amount",
            cell: (_, item) => {
                const amount = item.payable_amount;
                return amount != null ? (
                    <div className="text-nowrap fw-semibold">
                        {item.currency || "INR"} {parseFloat(amount).toLocaleString("en-IN")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Status",
            cell: (_, item) =>
                item.status ? (
                    <StatusChip
                        bg={getPaymentStatusBg(item.status)}
                        pill
                        title={item.status}
                        className="text-capitalize"
                    />
                ) : (
                    "-"
                ),
        },
        {
            header: "Transaction ID",
            cell: (_, item) => {
                const id =
                    item.payment_id ||
                    item.cf_payment_id ||
                    item.payment_details?.payment_id;
                return id ? (
                    <div className="text-nowrap" style={{ fontSize: "13px", fontFamily: "monospace" }}>
                        {id}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Payment Method",
            cell: (_, item) => {
                const group = item.payment_group || item.payment_method?.type;
                const details = item.payment_details || item.payment_method;
                return (
                    <div className="d-flex flex-column gap-1 text-nowrap">
                        <span className="text-capitalize fw-medium">{group || "-"}</span>
                        {details && (
                            <small className="text-muted">
                                {parsePaymentMethodDetails(details, group)}
                            </small>
                        )}
                    </div>
                );
            },
        },
        {
            header: "Service Charge",
            cell: (_, item) => {
                const val = item.service_charge;
                return val != null ? (
                    <div className="text-nowrap">
                        {item.currency || "INR"} {parseFloat(val).toLocaleString("en-IN")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Service Tax",
            cell: (_, item) => {
                const val = item.service_tax;
                return val != null ? (
                    <div className="text-nowrap">
                        {item.currency || "INR"} {parseFloat(val).toLocaleString("en-IN")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Settlement Amount",
            cell: (_, item) => {
                const val = item.settlement_amount;
                return val != null ? (
                    <div className="text-nowrap fw-medium">
                        {item.currency || "INR"} {parseFloat(val).toLocaleString("en-IN")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Error Details",
            cell: (_, item) => {
                const err = item.error_details;
                if (!err) return "-";

                const code = err.error_code || err.code || null;
                const desc = err.error_description || err.description || null;
                const reason = err.error_reason || err.reason || null;
                const fullText = typeof err === "string" ? err : JSON.stringify(err, null, 2);

                if (typeof err === "string") {
                    return (
                        <small
                            className="text-danger"
                            style={{ maxWidth: "220px", wordBreak: "break-word", cursor: "help" }}
                            title={err}
                        >
                            {err}
                        </small>
                    );
                }

                return (
                    <div
                        className="d-flex flex-column fs-sm text-danger"
                        style={{ maxWidth: "220px", cursor: "help" }}
                        title={fullText}
                    >
                        {code && (
                            <span className="fw-semibold text-nowrap">{code}</span>
                        )}
                        {desc && (
                            <span style={{ wordBreak: "break-word", whiteSpace: "normal" }}>{desc}</span>
                        )}
                        {reason && (
                            <span className="text-muted text-nowrap">Reason: {reason}</span>
                        )}
                        {!code && !desc && !reason && (
                            <span
                                style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            >
                                {fullText}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            header: "Paid At",
            cell: (_, item) =>
                item.paid_at ? (
                    <div className="d-flex flex-column text-nowrap">
                        <span>{formatMomentDate(item.paid_at, "DD MMM YYYY")}</span>
                        <small className="text-muted">{formatMomentDate(item.paid_at, "hh:mm A")}</small>
                    </div>
                ) : (
                    "-"
                ),
        },
        {
            header: "Created At",
            cell: (_, item) =>
                item.created_at ? (
                    <div className="d-flex flex-column text-nowrap">
                        <span>{formatMomentDate(item.created_at, "DD MMM YYYY")}</span>
                        <small className="text-muted">{formatMomentDate(item.created_at, "hh:mm A")}</small>
                    </div>
                ) : (
                    "-"
                ),
        },
        {
            header: "Updated At",
            cell: (_, item) =>
                item.updated_at ? (
                    <div className="d-flex flex-column text-nowrap">
                        <span>{formatMomentDate(item.updated_at, "DD MMM YYYY")}</span>
                        <small className="text-muted">{formatMomentDate(item.updated_at, "hh:mm A")}</small>
                    </div>
                ) : (
                    "-"
                ),
        },
    ];

    const paginationConfig = {
        currentPage,
        totalPages: Math.ceil(total / limit),
        total,
        onPageChange: (page) => setCurrentPage(page),
        pageSize: limit,
        setPageSize: (newLimit) => setLimit({ value: newLimit, label: newLimit }),
        setCurrentPage,
        perPageOptions: [10, 20, 50, 100],
    };

    const actionList = [
        {
            title: "Download Invoice",
            isVisible: (row) => row.status === 'paid',
            isLoading: (row) => loadingId === row.id,
            handler: (row) => handleDownloadInvoice(row),
        },
    ];

    return (
        <>
            {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}

            <div className="p-3 d-flex flex-wrap align-items-center gap-3 bg-white row-gap-2 column-gap-3">
                <TableSearch
                    value={searchTerm}
                    placeholder="Search by name, email, mobile or transaction ID"
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                />
            </div>

            <CustomTable
                data={data}
                columns={columns}
                keyField="id"
                actionList={actionList}
                emptyMessage="No payment transactions found."
                tbody={{ trClass: "" }}
                pagination={paginationConfig}
            />
        </>
    );
};

export default List;
